import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostListener,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { ViewportService } from '@services/geo/viewport.service';
import { MenuService, PageAction } from '@services/shared/menu.service';
import { SharedModule } from '@shared/modules/shared.module';
import { Table } from 'primeng/table';
import { ButtonComponent } from '../button/button.component';
import { CardComponent } from '../card/card.component';
import { ChipComponent } from '../chip/chip.component';
import { InlineFilterField, InlineFiltersComponent } from '../inline-filters/inline-filters.component';
import { InputComponent } from '../input/input.component';
import { PaginatorComponent } from '../paginator/paginator.component';
import { SelectionActionBarComponent } from './selection-action-bar/selection-action-bar.component';
import { TableAction, TableColumn, TableFilterChip, TablePageEvent, TableRow, TableRowId } from './table.model';
import { TableActionMenuService } from './table-action-menu.service';
import { TableCellComponent } from './table-cell/table-cell.component';
import { cellText, getNestedValue } from './table-cell.util';
import { TableMobileListComponent } from './table-mobile-list/table-mobile-list.component';

@Component({
	selector: 'app-table',
	standalone: true,
	imports: [
		SharedModule,
		ButtonComponent,
		PaginatorComponent,
		CardComponent,
		ChipComponent,
		InputComponent,
		SelectionActionBarComponent,
		InlineFiltersComponent,
		TableCellComponent,
		TableMobileListComponent,
	],
	templateUrl: './table.component.html',
	styleUrl: './table.component.scss',
	// One floating menu per table, disposed with it
	providers: [TableActionMenuService],
})
export class TableComponent<T extends TableRow = TableRow> implements OnInit, OnChanges, OnDestroy {
	// ====== Inputs ======
	@Input() data: T[] = [];
	@Input() columns: TableColumn<T>[] = [];
	@Input() mainField = '';
	@Input() title = '';
	@Input() actionButtonLabel?: string;
	@Input() actionButtonVariant: 'primary' | 'secondary' | 'tertiary' | 'icon-only' | 'icon-ghost' | 'ghost' | 'link' =
		'ghost';
	@Input() actionButtonIcon: string = 'Plus';
	@Input() actionButtonShowLabelInMobile = false;
	// Secondary action button (displayed to the left of the main action button)
	@Input() showSecondaryActionButton = false;
	@Input() secondaryActionButtonLabel?: string;
	@Input() secondaryActionButtonIcon?: string;
	@Input() secondaryActionButtonVariant:
		| 'primary'
		| 'secondary'
		| 'tertiary'
		| 'icon-only'
		| 'icon-ghost'
		| 'ghost'
		| 'link' = 'ghost';
	@Input() showSecondaryActionButtonOnMobile = false; // Shows secondary action button on mobile/responsive screens
	@Input() showSearch = false;
	@Input() showFilter = false;
	@Input() showFilterOnMobile = true; // Shows filter button on mobile/responsive screens
	@Input() showActionButton = true;
	@Input() showOptionsButton = true;
	@Input() showNavigationButton = false;
	@Input() navigationButtonLabel = '';
	@Input() navigationButtonIcon = 'ChevronRight';
	@Input() hideHeader = false;
	@Input() hidePagination = false;
	@Input() hideCheckboxes = true;
	@Input() showMobileSearch = false;
	@Input() showMobileMenu = false;
	@Input() showSelectCheckbox = false;
	@Input() viewType: 'list' | 'card' = 'list';
	@Input() sortOrder = 1; // 1 ascending, -1 descending
	@Input() sortField = '';
	@Input() showViewToggleType = false;
	@Input() showPaginator = true;
	@Input() showActionButtonsDown = true;
	@Input() hideAccordionArrow = false; // Hides the expand/collapse arrow in mobile accordion view
	@Input() showMobileNavigateArrow = false; // Shows a navigate arrow when hideAccordionArrow is true
	@Input() showMobileDownloadButton = false; // Shows a download button in mobile accordion view
	@Input() showMobileCheckboxes = false; // Shows checkboxes on the left in mobile view
	@Input() mobileSubtitleField = ''; // Field to show as subtitle in mobile accordion header
	@Input() mobileSubtitleIcon = ''; // Lucide icon name to show next to the subtitle
	@Input() mobileSubtitleEmptyKey = ''; // Translation key to show when subtitle field is empty
	@Input() mobileAccordionFields: string[] = []; // Fields to show in accordion content (empty = all except mainField)
	@Input() actionBarType: 'default' | 'download' = 'download';
	@Input() userRole!: string;

	@Output() inlineFilterChange = new EventEmitter<{ key: string; value: unknown }>(); // Event when inline filter changes

	@Input() onFiltersChanged?: (filters: unknown) => void;
	/** Unused by the table itself; kept for the pages that still bind it */
	@Input() crudComponent?: { show: (...args: never[]) => unknown };
	@Input() rows = 0;
	@Input() totalRecords = 0;

	@Input() menuActions: TableAction[] = [];
	@Input() getMenuActions?: (rowData: T) => TableAction[];
	@Input() isLoading = false;
	/** The last load failed: shows an error state with a retry button instead of the empty state. */
	@Input() loadError = false;
	@Output() retryLoad = new EventEmitter<void>();
	@Input() lazy = true; // Enable/disable lazy loading (server-side pagination)

	// Action Button Menu - shows a dropdown menu instead of emitting click event
	@Input() actionButtonMenuActions: TableAction[] = [];

	// ====== Filter Chips ======
	@Input() filterChips: TableFilterChip[] = [];
	@Output() onRemoveChip = new EventEmitter<TableFilterChip>();

	/** Filters drawn as pills in the table header (replaces the filter popover + chips). */
	@Input() pillFilters: InlineFilterField[] = [];
	@Input() pillFiltersLoading = 0; // Skeleton pills shown while the page builds its filters (0 = none)
	@Input() pillFilterValues: Record<string, unknown> = {};

	// ====== Selection Mode ======
	isSelectionMode = false;

	// ====== Outputs ======
	@Output() onPageChange = new EventEmitter<TablePageEvent>();
	@Output() deleteItem = new EventEmitter<T | T[]>();
	@Output() editItem = new EventEmitter<T>();
	@Output() onSort = new EventEmitter<{ field: string }>();
	@Output() searchEvent = new EventEmitter<string>();
	@Output() onFilter = new EventEmitter<unknown>();
	@Output() sortByMainField = new EventEmitter<void>();

	@Output() navigationTriggered = new EventEmitter<T>(); // Event for navigation
	@Output() downloadTriggered = new EventEmitter<T>(); // Event for download action
	@Output() actionButtonClick = new EventEmitter<void>();
	@Output() secondaryActionButtonClick = new EventEmitter<void>(); // Event for secondary action button

	// ====== ViewChild References ======
	@ViewChild('dt') dt!: Table;

	// ====== UI State ======
	isMobile = false;
	wasMobile = false;

	// ====== Mobile infinite scroll ======
	// On mobile there is no paginator: the list keeps the pages already loaded and asks the
	// parent for the next one (same onPageChange event) when the end of the list comes into view.
	mobileItems: T[] = [];
	infiniteLoading = false;
	private infinitePage = 1; // pages already in mobileItems
	private infiniteRows = 0; // page size fixed when the list restarts
	private infiniteRestarting = false; // asked the parent for page 1 again: no more pages until it arrives
	/** Mobile cards view: watches the end of the list (sentinel) and emits nearEnd */
	@ViewChild(TableMobileListComponent) private mobileList?: TableMobileListComponent<T>;

	get hasMoreMobileItems(): boolean {
		return this.mobileItems.length < this.totalRecords;
	}

	showMobileActionMenu = false;
	mobileMenuActions: TableAction[] = [];
	mobileMenuEntityName = '';
	selectedItems: T[] = [];
	globalFilterFields: string[] = [];
	expandedRowKeys: { [key: string]: boolean } = {};
	isPopupOpen = false;

	// ====== Responsive Accordion State ======
	expandedItems = new Set<TableRowId>();

	// ====== Action Menu ======
	activeActionMenu: T | null = null;
	menuPosition: { top: string; right: string } = { top: '0px', right: '0px' };

	// ====== Action Button Menu State ======
	showActionButtonMenu = false;

	// ====== Search & Filter ======
	searchTerm = '';
	menuOpen = false;
	searchVisible = false;

	selectedRowForNavigation: T | null = null;

	/** A row action: data is the row, or the selected rows for the bulk actions (delete, download) */
	@Output() menuActionTriggered = new EventEmitter<{
		action: string;
		data: T | T[];
	}>();

	triggerMenu(row: T): void {
		this.menuActionTriggered.emit({ action: 'openMenu', data: row });
	}

	// Pagination
	first = 0;
	options = [
		{ label: '10', value: 10 },
		{ label: '20', value: 20 },
		{ label: '50', value: 50 },
	];

	constructor(
		public viewportService: ViewportService,
		private cdr: ChangeDetectorRef,
		private menuService: MenuService,
		private actionMenu: TableActionMenuService,
	) {}

	/**
	 * Lifecycle hook: OnInit
	 * Initializes viewport state and filters.
	 */
	/** Something to show above the table (tabs, filters, buttons, chips); otherwise no gap is left */
	get hasCaption(): boolean {
		const m = this.isMobile;
		return (
			this.pillFilters.length > 0 ||
			this.pillFiltersLoading > 0 ||
			(this.showSecondaryActionButton && (!m || this.showSecondaryActionButtonOnMobile)) ||
			(this.showActionButton && m) ||
			(m && (this.showMobileSearch || this.showMobileMenu)) ||
			this.showSearch ||
			(this.showFilter && (!m || this.showFilterOnMobile)) ||
			(!m && (this.showViewToggleType || this.showSelectCheckbox)) ||
			this.filterChips.length > 0
		);
	}

	ngOnInit() {
		this.wasMobile = this.viewportService.isMobileView();
		this.isMobile = this.wasMobile;

		// Generate global filter fields from columns
		this.globalFilterFields = this.columns.map((col) => col.field);

		this.syncPageAction();
	}

	ngOnDestroy(): void {
		this.releasePageAction();
		this.actionMenu.close();
	}

	// ====== Mobile infinite scroll ======

	/** New data from the parent: next page appended, anything else (filter, tab, sort, reload) restarts */
	private syncMobileItems(): void {
		const incoming = this.data ?? [];
		this.infiniteRestarting = false;
		if (this.infiniteLoading) {
			const known = new Set(this.mobileItems.map((i) => i?.id));
			this.mobileItems = [...this.mobileItems, ...incoming.filter((i) => !known.has(i?.id))];
			this.infinitePage++;
			this.infiniteLoading = false;
		} else {
			const wasScrolled = this.infinitePage > 1;
			this.mobileItems = [...incoming];
			this.infinitePage = 1;
			this.infiniteRows = this.rows || incoming.length;
			// A reload after scrolling (e.g. after an action) brings the parent's last page, not the
			// first: ask for page 1 so the list starts over (no-op for the parent if it is already there)
			if (wasScrolled && this.isMobile) {
				this.infiniteRestarting = true;
				this.first = 0;
				this.onPageChange.emit({ first: 0, rows: this.infiniteRows });
			}
		}
		// A short first page may leave the sentinel on screen: the observer won't fire again
		setTimeout(() => this.mobileList?.checkSentinelInView());
	}

	/** The mobile list reached its end: ask the parent for the next page (unless one is pending or none left) */
	loadNextMobilePage(): void {
		if (
			!this.isMobile ||
			this.infiniteLoading ||
			this.infiniteRestarting ||
			this.isLoading ||
			!this.hasMoreMobileItems ||
			!this.infiniteRows
		) {
			return;
		}
		this.infiniteLoading = true;
		const rows = this.infiniteRows;
		this.first = this.infinitePage * rows;
		this.onPageChange.emit({ first: this.first, rows });
	}

	// ====== Page action (topbar) ======

	private pageAction: PageAction | null = null;

	/**
	 * On desktop the add button lives in the topbar (profile icon bar), not in the table header.
	 * On mobile the table keeps rendering it.
	 */
	private syncPageAction(): void {
		this.releasePageAction();
		if (!this.showActionButton || this.isMobile) return;

		this.pageAction = {
			label: this.actionButtonLabel || 'Add',
			icon: this.actionButtonIcon,
			onClick: (origin) => this.openActionButtonMenu(origin),
		};
		this.menuService.setPageAction(this.pageAction);
	}

	private releasePageAction(): void {
		if (this.pageAction) {
			this.menuService.clearPageAction(this.pageAction);
			this.pageAction = null;
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['data']) {
			this.syncMobileItems();
		} else if (changes['isLoading'] && !this.isLoading && this.infiniteLoading) {
			// The page request ended without new data (error): let the user retry by scrolling
			setTimeout(() => {
				if (this.infiniteLoading && !this.isLoading) this.infiniteLoading = false;
			});
		}

		if (
			(changes['showActionButton'] || changes['actionButtonLabel'] || changes['actionButtonIcon']) &&
			!changes['showActionButton']?.firstChange
		) {
			this.syncPageAction();
		}

		// Columns often arrive after init (built once translations load): keep them in sync.
		if (changes['columns'] && !changes['columns'].firstChange) {
			this.globalFilterFields = this.columns.map((col) => col.field);
		}
	}

	onSearch(): void {
		this.searchEvent.emit(this.searchTerm); // Sends value to parent
	}

	toggleSearchOpen(): void {
		this.searchVisible = !this.searchVisible;
		if (!this.searchVisible) {
			this.searchTerm = '';
			this.searchEvent.emit('');
		}
	}

	clearSearch(): void {
		this.searchTerm = '';
		this.searchEvent.emit('');
	}

	onTableSort(event: { field?: string }): void {
		this.onSort.emit({ field: event.field as string });
	}

	// ====== Pagination Methods ======

	/**
	 * Emits page change event to the parent component.
	 * @param event The pagination event containing new page and rows per page
	 */
	onPaginate(event: TablePageEvent): void {
		this.first = event.first;
		this.rows = event.rows;
		this.onPageChange.emit(event);
	}

	// ====== Row Expansion Methods ======

	/**
	 * Handles row expansion.
	 * Clears previous expanded rows and only expands the selected row.
	 * @param event Row expansion event
	 */
	onRowExpand(event: { data: T }) {
		this.expandedRowKeys = {}; // Clear previously expanded rows
		this.expandedRowKeys[String(event.data.id)] = true; // Expand current row
	}

	/**
	 * Handles row collapse.
	 * Removes the collapsed row from the expanded rows.
	 * @param event Row collapse event
	 */
	onRowCollapse(event: { data: T }) {
		delete this.expandedRowKeys[String(event.data.id)]; // Remove row from expanded rows
	}

	/**
	 * Resets all expanded rows, useful when switching to desktop view.
	 */
	resetExpandedRows() {
		if (this.dt) {
			this.dt.expandedRowKeys = {}; // Clear expanded rows
		}
	}

	// ====== Responsive Methods ======

	/**
	 * Listens for window resize events and updates `isMobile` state.
	 * Resets expanded rows when switching from mobile to desktop.
	 */
	@HostListener('window:resize', [])
	onResize() {
		this.isMobile = this.viewportService.isMobileView();

		// Reset expanded rows if switching from mobile to desktop
		if (this.wasMobile && !this.isMobile) {
			this.resetExpandedRows();
		}

		// Dynamically update rows per page based on viewport
		this.rows = this.isMobile ? 8 : 10;

		if (this.wasMobile !== this.isMobile) {
			this.syncPageAction();
		}

		this.wasMobile = this.isMobile; // Update previous state
	}

	// ====== Floating Action Menu ======

	/**
	 * Closes the menu if clicked outside.
	 * @param event The click event on the document.
	 */
	@HostListener('document:click', ['$event'])
	onClickOutside(event: Event) {
		const target = event.target as HTMLElement;

		// Closes action menu on Desktop if click is outside
		if (!target.closest('.action-menu') && !target.closest('.ellipsis-button')) {
			this.activeActionMenu = null;
		}

		// Closes mobile menu if click is outside menu button or floating menu
		if (!target.closest('.ellipsis-button') && !target.closest('.mobile-menu')) {
			this.menuOpen = false;
		}

		// Closes search if click is outside search container
		if (this.searchVisible && !target.closest('.search-container')) {
			this.searchVisible = false;
			this.searchTerm = '';
			this.searchEvent.emit('');
		}
	}

	/**
	 * Closes the floating menu when clicking outside.
	 */
	closeMenu(): void {
		this.activeActionMenu = null;
	}

	// ====== Row Actions ======

	/**
	 * Emits an event to delete multiple selected clients.
	 * If only one client is selected, it emits a single delete event.
	 */
	deleteMultipleRows(): void {
		if (this.selectedItems.length === 0) {
			return;
		}

		// If only one client is selected, call normal event
		if (this.selectedItems.length === 1) {
			this.deleteItem.emit(this.selectedItems[0]);
		} else {
			// Send complete array for multiple deletion
			this.deleteItem.emit(this.selectedItems);
		}
		this.selectedItems = []; // Clears selected items
	}

	// ====== Popup Methods ======

	/**
	 * Opens the popup for adding a new item.
	 */
	openNewItem(): void {
		this.isPopupOpen = true;
	}

	/**
	 * Closes the popup.
	 */
	closePopup(): void {
		this.isPopupOpen = false;
	}

	// ====== Utility Methods ======

	/**
	 * Retrieves the header name for the main field displayed.
	 * @returns The header of the main field
	 */
	getMainFieldHeader(): string {
		const mainColumn = this.columns.find((col) => col.field === this.mainField);
		return mainColumn ? mainColumn.header : this.mainField;
	}

	/**
	 * Toggles menu visibility on mobile.
	 */
	toggleMenu() {
		this.menuOpen = !this.menuOpen;
	}
	openFilter(buttonElement: HTMLElement) {
		this.onFilter.emit({ action: 'open', button: buttonElement });
		this.menuOpen = false;
	}

	/**
	 * Emits event to sort by main column (`mainField`)
	 */
	triggerSortByMainField() {
		this.sortByMainField.emit(); // Calls function in parent component
		this.menuOpen = false; // Closes menu after sorting
	}

	/**
	 * Toggles search field visibility on mobile.
	 */
	toggleSearch(): void {
		this.searchVisible = !this.searchVisible;
	}

	@Output() rowSelected = new EventEmitter<T>();

	onRowClick(event: MouseEvent, rowData: T) {
		// Check if click was on action button, checkbox or action menu
		const target = event.target as HTMLElement;
		const isActionButton = target.closest('.actions-column') !== null;
		const isCheckbox =
			target.closest('.p-checkbox') !== null ||
			target.closest('.p-checkbox-box') !== null ||
			target.closest('p-tableCheckbox') !== null;
		const isActionMenu = target.closest('.action-menu') !== null;

		// Only emit event if click was not on action button, checkbox or menu
		if (!isActionButton && !isCheckbox && !isActionMenu) {
			this.rowSelected.emit(rowData);
			this.selectedRowForNavigation = rowData;
		} else {
			event.stopPropagation(); // Stop event propagation
		}
	}

	/**
	 * Simple row selection handler for mobile accordion without expand functionality
	 * @param rowData The row data object
	 */
	onRowSelect(rowData: T) {
		this.rowSelected.emit(rowData);
		this.selectedRowForNavigation = rowData;
	}

	onDownloadClick(rowData: T) {
		this.downloadTriggered.emit(rowData);
	}

	// Method for items per page change
	onRowsPerPageChange(event: { value: number }) {
		this.first = 0; // Reset to first page
		this.rows = event.value;
		this.onPaginate({ first: 0, rows: this.rows });
	}

	// ====== ACTION MENU ======

	/** An action from a card's menu: its data is the card's row */
	onMenuAction(event: { action: string; data: unknown }): void {
		this.menuActionTriggered.emit({ action: event.action, data: event.data as T });
		this.resetSelection();
	}

	/**
	 * Resets table selection after actions
	 */
	private resetSelection(): void {
		// Clear selection array
		this.selectedItems = [];

		// Clear PrimeNG internal selection
		if (this.dt) {
			this.dt.selection = [];
			this.dt.updateSelectionKeys(); // Force selection update
		}

		// Close the menu
		this.activeActionMenu = null;
	}

	/**
	 * Handles mobile checkbox change
	 */
	onMobileCheckboxChange(item: T): void {
		if (item._selected) {
			// Add to selection if not already selected
			if (!this.selectedItems.includes(item)) {
				this.selectedItems.push(item);
			}
		} else {
			// Remove from selection
			const index = this.selectedItems.indexOf(item);
			if (index > -1) {
				this.selectedItems.splice(index, 1);
			}
		}
		this.cdr.detectChanges();
	}

	resetFloatingMenus(): void {
		this.resetSelection();
	}

	/**
	 * Handles bottom action buttons (delete/edit) and automatically resets selection
	 */
	onBottomAction(action: string, data: T | T[]): void {
		this.menuActionTriggered.emit({ action, data });
		this.resetSelection();
	}

	/**
	 * Handles action bar events from the SelectionActionBarComponent
	 */
	handleActionBarAction(event: { type: string; data?: unknown }): void {
		switch (event.type) {
			case 'delete':
				this.onBottomAction('delete', this.selectedItems);
				break;
			case 'edit':
				this.onBottomAction('update', this.selectedItems[0]);
				break;
			case 'download':
				this.onBottomAction('download', this.selectedItems);
				break;
			case 'cancel':
				this.cancelSelectionMode();
				break;
		}
	}

	openActionMenu(origin: EventTarget | null, actions: TableAction[], rowData: T) {
		// 📱 Mobile: Show bottom sheet modal
		if (this.isMobile) {
			this.mobileMenuActions = actions;
			this.mobileMenuEntityName = cellText(getNestedValue(rowData, this.mainField)) ?? '';
			this.showMobileActionMenu = true;
			return;
		}

		// 🖥️ Desktop: floating menu under the button (the target may be the SVG inside it)
		let element: HTMLElement | null = null;
		if (origin instanceof HTMLElement) {
			element = origin;
		} else if (origin instanceof SVGElement) {
			element = origin.closest('button') || (origin.closest('app-button') as HTMLElement);
		}
		if (element) this.actionMenu.open(element, actions, rowData);
	}

	/** The mobile bottom sheet closes with Escape, like the dialogs. */
	@HostListener('document:keydown.escape')
	onEscapeMobileActionMenu(): void {
		if (this.showMobileActionMenu) {
			this.closeMobileActionMenu();
		}
	}

	closeMobileActionMenu(): void {
		this.showMobileActionMenu = false;
		this.mobileMenuActions = [];
		this.mobileMenuEntityName = '';
	}

	/**
	 * Opens the action button menu (dropdown for add button)
	 */
	openActionButtonMenu(origin: EventTarget | null): void {
		if (this.actionButtonMenuActions.length === 0) {
			// No menu actions, emit normal click
			this.actionButtonClick.emit();
			return;
		}

		// The target may be any element inside the button (icon, span, svg...)
		let element: HTMLElement | null = null;
		if (origin instanceof HTMLElement) {
			element =
				(origin.closest('button') as HTMLElement) ||
				(origin.closest('app-button') as HTMLElement) ||
				(origin.closest('p-button') as HTMLElement) ||
				origin;
		} else if (origin instanceof SVGElement) {
			element =
				(origin.closest('button') as HTMLElement) ||
				(origin.closest('app-button') as HTMLElement) ||
				(origin.closest('p-button') as HTMLElement);
		}
		if (element) this.actionMenu.open(element, this.actionButtonMenuActions);
	}

	truncateHtml(html: string, maxLength: number): string {
		// Only run in browser
		if (typeof document === 'undefined') {
			// Fallback for SSR - use simple regex to remove tags
			const text = html.replace(/<[^>]*>/g, '');
			if (text.length <= maxLength) return html;
			const truncated = `${text.slice(0, maxLength)}...`;
			return `<span title="${text.replace(/"/g, '&quot;')}">${truncated}</span>`;
		}

		// Remove tags to count only text
		const div = document.createElement('div');
		div.innerHTML = html;
		const text = div.textContent || div.innerText || '';
		if (text.length <= maxLength) return html;

		// If longer, truncate text and put back in simple HTML
		const truncated = `${text.slice(0, maxLength)}...`;
		return `<span title="${text.replace(/"/g, '&quot;')}">${truncated}</span>`;
	}

	// ====== Responsive Accordion Methods ======

	/**
	 * Toggles the expansion state of an accordion item
	 * @param id The ID of the item to toggle
	 */
	toggleItem(id: TableRowId | undefined): void {
		if (id === undefined) return;
		if (this.expandedItems.has(id)) {
			this.expandedItems.delete(id);
		} else {
			this.expandedItems.add(id);
		}
		this.cdr.detectChanges();
	}

	/**
	 * Handles navigation from accordion items
	 * @param item The item that triggered navigation
	 */
	onNavigate(item: T): void {
		this.navigationTriggered.emit(item);
	}

	/**
	 * Checks if any column has showAvatar property
	 * Used for skeleton display
	 */
	hasAvatarColumn(): boolean {
		return this.columns.some((col) => col.showAvatar);
	}

	// ====== Selection Mode Methods ======

	/**
	 * Toggles selection mode (shows/hides checkboxes)
	 */
	toggleSelectionMode(): void {
		this.isSelectionMode = !this.isSelectionMode;

		// If exiting selection mode, clear all selections
		if (!this.isSelectionMode) {
			this.resetSelection();
		}

		this.menuOpen = false; // Close mobile menu
		this.cdr.detectChanges();
	}

	/**
	 * Gets the count of selected items
	 */
	get selectedCount(): number {
		return this.selectedItems.length;
	}

	/**
	 * Checks if we should show checkboxes (based on hideCheckboxes input or selection mode)
	 */
	get shouldShowCheckboxes(): boolean {
		return !this.hideCheckboxes || this.isSelectionMode;
	}

	/**
	 * Cancels selection mode and clears selections
	 */
	cancelSelectionMode(): void {
		this.isSelectionMode = false;
		this.resetSelection();
		this.cdr.detectChanges();
	}
}
